import React, { useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Grid,
  Link,
} from "@mui/material";
import { Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material";
import DescriptionIcon from '@mui/icons-material/Description';
import { convertMillisecondsToDate } from "../../hooks/helper";
import useConfirmationDialog from "../../hooks/useConfirmationDialog";

const IconBox = ({ quantity, label, active, onClick }) => (
  <Grid item xs={12} sm={6} md={4}>
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        p: 2,
        bgcolor: active ? "#ffe5e5" : "#f0f0f0",
        border: active ? "2px solid #f44336" : "1px solid transparent",
        borderRadius: 2,
        cursor: "pointer",
        '&:hover': {
          bgcolor: "#e0e0e0"
        }
      }}
    >
      <Box sx={{ p: 1, bgcolor: "white", borderRadius: 1, mr: 2 }}>
        <DescriptionIcon fontSize="large" color="error" />
      </Box>
      <Box>
        <Typography variant="h6">{quantity}</Typography>
        <Typography variant="body2" color={active ? "textPrimary" : "textSecondary"}>{label}</Typography>
      </Box>
    </Box>
  </Grid>
);

const OrderListing = ({
  isLoading,
  orders,
  handleEditOpen,
  handleRemoveOrder,
  activePage,
  setActivePage,
  totalPages,
  fetchTrucksData,
  orderStageCount,
}) => {
  const [activeState, setActiveState] = useState(null);
  const { openConfirmationDialog, ConfirmationDialog } = useConfirmationDialog(handleRemoveOrder);

  const handleNextPage = () => {
    if (activePage < totalPages) {
      const page = activePage + 1;
      setActivePage(page);
      fetchTrucksData(page);
    }
  };

  const handlePrevPage = () => {
    if (activePage > 1) {
      const page = activePage - 1;
      setActivePage(page);
      fetchTrucksData(page);
    }
  };

  const sections = [
    { label: "Trip Initiated", ids: [1, 2] },
    { label: "Parking Seller", ids: [3, 4] },
    { label: "In Transit", ids: [5, 6] },
    { label: "Parking Buyer", ids: [7] },
    { label: "Delivered Qty", ids: [8, 9] },
  ];

  const Headers = [
    "PO No",
    "PO Type",
    "Po Date",
    "Est Delivery Date",
    "Buyer",
    "Seller",
    "Mineral Name",
    "Ordered Qty",
    "Qty Delivered",
    "Trip Alloted",
    "Actions",
  ]

  return (
    <>
      {ConfirmationDialog}
      <Grid container spacing={2} sx={{ my: 2 }}>
      <IconBox
          onClick={() => setActiveState(0)}
          quantity={orderStageCount?.reduce(
            (acc, curr) => acc + curr.totalWeight,
            0
          )}
          label="Total qty"
          active={activeState === 0}
        />
        {sections.map((item, index) => {
          const qty = orderStageCount
            ?.filter((f) => item.ids.includes(f.id))
            .reduce((acc, curr) => acc + curr.totalWeight, 0)
          
          return (
            <IconBox
              key={index}
              quantity={qty}
              label={item.label}
              active={activeState === (index+1)}
              onClick={() => setActiveState(index+1)}
            />
          );
        })}
      </Grid>

      <TableContainer component={Paper} sx={{ overflowX: 'auto', my: 2 }}>
        <Table stickyHeader>
          <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
            <TableRow>
              {Headers.map((label) => (
                <TableCell
                  key={label}
                  sx={{ fontWeight: "bold", minWidth: 150, backgroundColor: '#f5f5f5' }}
                >
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={10} align="center">Loading...</TableCell>
              </TableRow>
            ) : (
              orders?.map((order) => (
                <TableRow key={order.id} hover>
                  <TableCell>
                    <Link href={`/trip?selectedOrder=${order.orderId}`}>
                      {order?.poNumber}
                    </Link>
                  </TableCell>
                  <TableCell>{' '}</TableCell>
                  <TableCell>{convertMillisecondsToDate(order?.poDate)}</TableCell>
                  <TableCell>{convertMillisecondsToDate(order?.expectedDeliveryDate)}</TableCell>
                  <TableCell>{order?.buyer?.buyerName}</TableCell>
                  <TableCell>{order?.seller?.mineName}</TableCell>
                  <TableCell>{order?.material?.materialName}</TableCell>
                  <TableCell>{order?.totalQuantity}</TableCell>
                  <TableCell>{order?.totalDeliveredQuantity}</TableCell>
                  <TableCell>{order?.tripCount}</TableCell>
                  <TableCell>
                    <IconButton color="primary" onClick={() => handleEditOpen(order)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton color="error" onClick={() => openConfirmationDialog(order)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
        <Button variant="outlined" onClick={handlePrevPage} disabled={activePage === 1}>
          Previous
        </Button>
        <Typography variant="body2">Page {activePage} of {totalPages}</Typography>
        <Button variant="outlined" onClick={handleNextPage} disabled={activePage === totalPages}>
          Next
        </Button>
      </Box>
    </>
  );
};

export default OrderListing;
